// 章の地図を Leaflet 非依存で表す「地図の仕様」。各章のコンテンツ・ビルダーがこれを組み立て、
// 画面では OverviewMiniMap/ChapterMap が Leaflet で描き、PDF では pdf/render-map.ts が canvas に描く。
// マーカーは L.DivIcon を持たず {icon|label, color} の記述にして、両レンダラーが同じ見た目を再現する。
import type { PathOptions } from 'leaflet';
import type { IconName } from '@/components/ui/Icon/Icon';
import type { Point } from '@/lib/api.types';

export type GeometryLayer = {
  data: GeoJSON.Geometry;
  /** Leaflet の PathOptions。PDF ラスタライザも Leaflet の既定値 (stroke/fill/opacity) を再現する。 */
  style: PathOptions;
  label?: string;
};

/** 価格の吹き出しを点のどちら側に置くか (本体の位置。しっぽは本体から点へ伸びる)。 */
export type LabelSide = 'above' | 'below' | 'right' | 'left';

export type MarkerSpec = {
  point: Point;
  /** 円・吹き出し・丸の塗り色。アイコンは雲色 (#F2F2F2) で描く (lib/leaflet-setup.ts と同じ)。 */
  color: string;
  /** Tabler アイコン名 (施設・ハザード)。28px の円に雲色のアイコン。 */
  icon?: IconName;
  /**
   * 価格の吹き出し (「62万円/㎡」など)。点の真上 (labelSide が 'below' なら真下) に color で塗った
   * 小札を出し、しっぽの先が点の座標。icon とは排他。寸法と重なりの間引きは lib/map-labels.ts で、
   * 画面 (lib/leaflet-setup.ts) と PDF (pdf/render-map.ts) が同じ見た目で描く。
   * icon も label も無い点は小さな丸 (吹き出しが重なって落とされた価格点)。
   */
  label?: string;
  /** 吹き出しを出す側 (既定 'above')。cullOverlappingLabels が決める。 */
  labelSide?: LabelSide;
  /** ツールチップ (画面のみ)。 */
  title?: string;
};

export type LegendItem = {
  color: string;
  label: string;
  /** Tabler アイコン名（絵文字の代替・§5）。指定時はアイコンで描画する。 */
  icon?: IconName;
  /** 「…」などの記号（凡例の省略表示）。 */
  emoji?: string;
  outline?: boolean;
};

// Continuous color-scale legend (e.g. the price heatmap): a gradient bar with end labels.
// Used when a value is mapped onto a ramp rather than into discrete labeled bands.
export type LegendGradient = {
  colors: readonly string[];
  minLabel: string;
  maxLabel: string;
};

export type MapSpec = {
  /** 章内で一意なキー (React key と PDF の画像キーに使う)。 */
  key: string;
  point: Point;
  tileUrl: string;
  /** 半透明で重ねる公式ラスター (水害ハザード)。 */
  overlayTileUrl?: string;
  overlayOpacity?: number;
  zoom: number;
  /** 画面での高さ (px)。PDF は比率を保って縮小する。 */
  height: number;
  /** 中心点と全ピンが収まるよう表示範囲を広げる。zoom は「これ以上寄らない上限」になる。 */
  fitToMarkers?: boolean;
  /** 調査地点のピン (既定 true)。 */
  showCenterMarker?: boolean;
  geometries: GeometryLayer[];
  markers: MarkerSpec[];
  legend: LegendItem[];
  legendGradient?: LegendGradient;
  caption?: string;
  /** 面・点・ラスターのいずれも無いときに地図の下に出す文。 */
  emptyMessage?: string;
};

// fitToMarkers の規則 (画面の OverviewMiniMap と PDF の render-map が共有)。
// 引きの下限: 航空写真(seamlessphoto)は z14 未満で衛星モザイクに落ちるため、これより引かない。
// 余白はオーバーレイ(左上キャプション・右下凡例)とピンの重なりを避ける分。
export const FIT_MIN_ZOOM = 14;
export const FIT_PADDING_TOP_LEFT: [number, number] = [40, 44];
export const FIT_PADDING_BOTTOM_RIGHT: [number, number] = [72, 64];

export function mapHasContent(spec: MapSpec): boolean {
  return spec.geometries.length > 0 || spec.markers.length > 0 || spec.overlayTileUrl != null;
}

export function mapHasLegend(spec: MapSpec): boolean {
  return spec.legend.length > 0 || spec.legendGradient != null;
}
